/**
 * Auth slice — FlowRide access token and transient OAuth PKCE state.
 *
 * @remarks
 * Stored via @aparajita/capacitor-secure-storage (Keychain / Keystore on native).
 * Web builds use the plugin's localStorage fallback (not encrypted).
 */

import { z } from "zod";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/** Zod schema for authentication session fields. */
const AuthStateSchema = z.object( {
    "accessToken": z.string().nullable().
        default( null ),
    "pkceCodeVerifier": z.string().nullable().
        default( null ),
    "oauthState": z.string().nullable().
        default( null ),
} );

/** Persisted auth session (access token + PKCE transient fields). */
export type AuthState = z.infer<typeof AuthStateSchema>;

/** Default unauthenticated state. */
export const AUTH_DEFAULT: AuthState = AuthStateSchema.parse( {} );

/**
 * Auth slice (accessToken + PKCE fields).
 */
export const authSlice = definePersistedSlice<AuthState>( {
    "key":            "auth",
    "currentVersion": 1,
    "storage":        "secure",
    "eager":          true,
    "schema":         AuthStateSchema,
    "default":        () => AUTH_DEFAULT,
    "migrations":     {},
} );
